import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { routeService } from '../../services/route.service';
import { Route } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { RouteFormModal } from './RouteFormModal';
import toast from 'react-hot-toast';

export const RoutesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: routes, isLoading } = useQuery({
    queryKey: ['routes'],
    queryFn: routeService.getAll,
  });

  const deleteMutation = useMutation({
    mutationFn: routeService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      toast.success('Route deactivated successfully');
    },
    onError: () => {
      toast.error('Failed to deactivate route');
    },
  });

  const handleCreate = () => {
    setSelectedRoute(null);
    setIsModalOpen(true);
  };

  const handleEdit = (route: Route) => {
    setSelectedRoute(route);
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this route?')) {
      deleteMutation.mutate(id);
    }
  };

  const filteredRoutes = routes?.filter((route) =>
    route.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.destination.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">Route Management</h1>
          <p className="text-gray-600">Manage your transport routes</p>
        </div>
        <Button onClick={handleCreate} variant="primary">
          <Plus size={20} className="mr-2" />
          Add Route
        </Button>
      </div>

      <Card>
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by origin or destination..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading routes...</p>
          </div>
        ) : filteredRoutes && filteredRoutes.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Origin</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Distance (km)</TableHead>
                <TableHead>Round Trip</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRoutes.map((route) => (
                <TableRow key={route.id}>
                  <TableCell className="font-medium">{route.origin}</TableCell>
                  <TableCell>{route.destination}</TableCell>
                  <TableCell>{route.officialDistanceKm}</TableCell>
                  <TableCell>
                    <Badge variant={route.roundTrip ? 'info' : 'default'}>
                      {route.roundTrip ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{route.description || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={route.active ? 'success' : 'error'}>
                      {route.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(route)}
                        className="p-1 text-primary hover:bg-blue-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {route.active && (
                        <button
                          onClick={() => handleDelete(route.id)}
                          className="p-1 text-error hover:bg-red-50 rounded transition-colors"
                          title="Deactivate"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No routes found</p>
          </div>
        )}
      </Card>

      <RouteFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        route={selectedRoute}
      />
    </div>
  );
};
